import { MultiSelectFilter, type MultiSelectOption } from '../../components/ui/MultiSelectFilter';
import { SelectField } from '../../components/ui/SelectField';
import { Combobox } from '../../components/ui/Combobox';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { inputValueToCents, centsToInputValue } from '../../lib/money';
import type { CurrentUser } from '../../types/user';
import type {
  AccountGridFilters,
  AccountManagerFilter,
  BudgetFilter,
} from '../../types/accountGrid';
import { ACCOUNT_MISSING_FIELDS } from '../../lib/accountMissingFields';
import { SERVICE_VALUES, serviceLabel } from '../../lib/accountServices';

// Stage 5A §3.3 — multi-criteria filter bar. Filters AND-combine on the server.
// Quick-filter buttons set the canonical named filters (§3.3); parentAccountIds
// is supported by the API but intentionally has no UI control in v1 (deferred
// to ux-debt -- needs a searchable all-accounts picker).

type Props = {
  filters: AccountGridFilters;
  onChange: (next: AccountGridFilters) => void;
  users: CurrentUser[];
  // Brand-type accounts to populate the Brand filter ({ value: id, label: name }).
  brandOptions: MultiSelectOption[];
  // Distinct authoritative countries present in the roster, for the Country
  // filter (§3a). Free text, so data-derived like brandOptions.
  countryOptions: MultiSelectOption[];
  currentUserUid: string;
};

const ACCOUNT_TYPES = ['brand', 'group', 'location', 'head_office', 'region', 'dealership'];
const INDUSTRIES = ['fitness_franchise', 'automotive_franchise', 'small_business_ecommerce', 'other'];
const STATUSES = ['active', 'paused', 'cancelled'];
const CURRENCIES = ['AUD', 'NZD', 'USD', 'GBP', 'SGD'];
const MEDIA_SPEND = ['client', 'kyck'];
// Service filter options — from the shared catalogue (Phase 2b: content →
// content_creation, + community_management), labelled via serviceLabel.
const SERVICE_OPTIONS: MultiSelectOption[] = SERVICE_VALUES.map((v) => ({
  value: v,
  label: serviceLabel(v),
}));
// Derived from the single-source presence-field list (covers every scalar
// id/URL field on the Account) so a newly added identifier surfaces here
// automatically.
const MISSING_FIELDS: Array<{ value: string; label: string }> = ACCOUNT_MISSING_FIELDS.map(
  (f) => ({ value: f.key, label: f.label }),
);
// Connection-gap finder: each value is an ApiConnectionPlatform; a row matches
// when EVERY selected platform is NOT connected (per the stored block).
const NOT_CONNECTED_PLATFORMS: Array<{ value: string; label: string }> = [
  { value: 'metaads', label: 'Meta Ad Account' },
  { value: 'facebook', label: 'Facebook Page' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'googlebusiness', label: 'Google My Business' },
  { value: 'googleads', label: 'Google Ads' },
  { value: 'tiktok', label: 'TikTok' },
];

const AM_ANY = '__any__';
const AM_UNASSIGNED = '__unassigned__';

function opts(values: string[]): MultiSelectOption[] {
  return values.map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));
}

export function AccountsFilterBar({ filters, onChange, users, brandOptions, countryOptions, currentUserUid }: Props) {
  // Patch helper: shallow-merge, dropping keys set to undefined.
  const patch = (p: Partial<AccountGridFilters>) => {
    const next: AccountGridFilters = { ...filters, ...p };
    (Object.keys(next) as Array<keyof AccountGridFilters>).forEach((k) => {
      const v = next[k];
      if (v === undefined || (Array.isArray(v) && v.length === 0)) delete next[k];
    });
    onChange(next);
  };

  // --- AM filter: a single select over All / Any / Unassigned / specific user.
  const amValue: string =
    filters.accountManager?.mode === 'any'
      ? AM_ANY
      : filters.accountManager?.mode === 'unassigned'
        ? AM_UNASSIGNED
        : filters.accountManager?.mode === 'specific'
          ? filters.accountManager.uids[0] ?? ''
          : '';
  const onAmChange = (value: string) => {
    let accountManager: AccountManagerFilter | undefined;
    if (value === '') accountManager = undefined;
    else if (value === AM_ANY) accountManager = { mode: 'any' };
    else if (value === AM_UNASSIGNED) accountManager = { mode: 'unassigned' };
    else accountManager = { mode: 'specific', uids: [value] };
    patch({ accountManager });
  };

  // --- tri-state boolean select.
  const triValue = (v: boolean | undefined) => (v === undefined ? '' : v ? 'yes' : 'no');
  const onTri = (key: 'runsMeta' | 'runsGoogle', raw: string) =>
    patch({ [key]: raw === '' ? undefined : raw === 'yes' } as Partial<AccountGridFilters>);

  // --- quick filters (§3.3 named presets, computed not persisted).
  const applyMyAccounts = () =>
    onChange({ accountManager: { mode: 'specific', uids: [currentUserUid] } });
  const applyUnassignedActive = () =>
    onChange({ accountManager: { mode: 'unassigned' }, status: ['active'] });
  const applyActiveMetaNoFb = () =>
    onChange({ status: ['active'], runsMeta: true, missing: ['fbAdAccountId'] });

  const hasFilters = Object.keys(filters).length > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500">Quick:</span>
        <QuickButton label="My accounts" onClick={applyMyAccounts} />
        <QuickButton label="Unassigned active" onClick={applyUnassignedActive} />
        <QuickButton label="Active Meta, no FB Ad Account" onClick={applyActiveMetaNoFb} />
        {hasFilters && (
          <button
            type="button"
            onClick={() => onChange({})}
            className="text-xs text-slate-500 hover:text-slate-900 underline ml-1"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <MultiSelectFilter
          label="Type"
          options={opts(ACCOUNT_TYPES)}
          selected={filters.accountType ?? []}
          onChange={(v) => patch({ accountType: v as AccountGridFilters['accountType'] })}
        />
        <MultiSelectFilter
          label="Brand"
          options={brandOptions}
          selected={filters.brandAccountIds ?? []}
          onChange={(v) => patch({ brandAccountIds: v })}
        />
        <MultiSelectFilter
          label="Industry"
          options={opts(INDUSTRIES)}
          selected={filters.industry ?? []}
          onChange={(v) => patch({ industry: v as AccountGridFilters['industry'] })}
        />
        <MultiSelectFilter
          label="Status"
          options={opts(STATUSES)}
          selected={filters.status ?? []}
          onChange={(v) => patch({ status: v as AccountGridFilters['status'] })}
        />
        <MultiSelectFilter
          label="Currency"
          options={opts(CURRENCIES)}
          selected={filters.currency ?? []}
          onChange={(v) => patch({ currency: v as AccountGridFilters['currency'] })}
        />
        <MultiSelectFilter
          label="Country"
          options={countryOptions}
          selected={filters.country ?? []}
          onChange={(v) => patch({ country: v })}
        />
        <MultiSelectFilter
          label="Media Spend"
          options={opts(MEDIA_SPEND)}
          selected={filters.mediaSpendPaidBy ?? []}
          onChange={(v) => patch({ mediaSpendPaidBy: v as AccountGridFilters['mediaSpendPaidBy'] })}
        />
        <MultiSelectFilter
          label="Services"
          options={SERVICE_OPTIONS}
          selected={filters.services ?? []}
          onChange={(v) => patch({ services: v as AccountGridFilters['services'] })}
        />
        <MultiSelectFilter
          label="Missing"
          options={MISSING_FIELDS}
          selected={filters.missing ?? []}
          onChange={(v) => patch({ missing: v as AccountGridFilters['missing'] })}
        />
        <MultiSelectFilter
          label="Has set"
          options={MISSING_FIELDS}
          selected={filters.present ?? []}
          onChange={(v) => patch({ present: v as AccountGridFilters['present'] })}
        />
        <MultiSelectFilter
          label="Not connected"
          options={NOT_CONNECTED_PLATFORMS}
          selected={filters.notConnected ?? []}
          onChange={(v) => patch({ notConnected: v as AccountGridFilters['notConnected'] })}
        />
        <CheckboxControl
          label="Shared ad account — match token missing"
          checked={filters.sharedAdAccountMissingToken ?? false}
          onChange={(on) => patch({ sharedAdAccountMissingToken: on ? true : undefined })}
        />

        <SelectControl
          label="AM"
          searchable
          value={amValue}
          onChange={onAmChange}
          options={[
            { value: '', label: 'All AMs' },
            { value: AM_ANY, label: 'Any AM (assigned)' },
            { value: AM_UNASSIGNED, label: 'Unassigned' },
            ...users.map((u) => ({
              value: u.uid,
              label: formatUserDisplayName(u, { full: true }),
            })),
          ]}
        />
        <SelectControl
          label="Runs Meta"
          value={triValue(filters.runsMeta)}
          onChange={(v) => onTri('runsMeta', v)}
          options={[
            { value: '', label: 'Any' },
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
        />
        <SelectControl
          label="Runs Google"
          value={triValue(filters.runsGoogle)}
          onChange={(v) => onTri('runsGoogle', v)}
          options={[
            { value: '', label: 'Any' },
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
        />

        <BudgetControl
          label="Meta $"
          value={filters.metaBudget}
          onChange={(b) => patch({ metaBudget: b })}
        />
        <BudgetControl
          label="Google $"
          value={filters.googleBudget}
          onChange={(b) => patch({ googleBudget: b })}
        />
      </div>
    </div>
  );
}

function QuickButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
    >
      {label}
    </button>
  );
}

// Boolean toggle filter (§3b attribution gap). Absent when off so it never shows
// as an active filter chip; present-and-true isolates the shared-no-token rows.
function CheckboxControl({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 rounded border-slate-300"
      />
      {label}
    </label>
  );
}

function SelectControl({
  label,
  value,
  onChange,
  options,
  searchable,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  // Long dynamic lists (e.g. the AM roster) get a searchable, scrollable
  // combobox; short fixed selects (Runs Meta/Google) stay on SelectField.
  searchable?: boolean;
}) {
  return (
    <div className="inline-flex items-center gap-1 text-sm">
      <span className="text-xs text-slate-500">{label}</span>
      {searchable ? (
        <Combobox
          size="sm"
          aria-label={label}
          searchPlaceholder="Search…"
          value={value}
          onValueChange={onChange}
          options={options}
          className="max-w-[180px]"
        />
      ) : (
        <SelectField
          size="sm"
          aria-label={label}
          value={value}
          onValueChange={onChange}
          options={options}
          className="max-w-[180px]"
        />
      )}
    </div>
  );
}

// Budget filter: mode select + a dollar input shown for gt/lt. Empty -> no
// filter. Dollars convert to integer cents via the money helper.
function BudgetControl({
  label,
  value,
  onChange,
}: {
  label: string;
  value: BudgetFilter | undefined;
  onChange: (b: BudgetFilter | undefined) => void;
}) {
  const mode = value?.mode ?? '';
  const amount = value && (value.mode === 'gt' || value.mode === 'lt') ? centsToInputValue(value.cents) : '';

  const onMode = (m: string) => {
    if (m === '') onChange(undefined);
    else if (m === 'has') onChange({ mode: 'has' });
    else if (m === 'none') onChange({ mode: 'none' });
    else if (m === 'gt') onChange({ mode: 'gt', cents: value && 'cents' in value ? value.cents : 0 });
    else if (m === 'lt') onChange({ mode: 'lt', cents: value && 'cents' in value ? value.cents : 0 });
  };
  const onAmount = (raw: string) => {
    if (mode !== 'gt' && mode !== 'lt') return;
    const cents = inputValueToCents(raw) ?? 0;
    onChange({ mode, cents });
  };

  return (
    <div className="inline-flex items-center gap-1 text-sm">
      <span className="text-xs text-slate-500">{label}</span>
      <SelectField
        size="sm"
        aria-label={label}
        value={mode}
        onValueChange={onMode}
        options={[
          { value: '', label: 'Any' },
          { value: 'has', label: 'Has budget' },
          { value: 'none', label: 'No budget' },
          { value: 'gt', label: '>' },
          { value: 'lt', label: '<' },
        ]}
      />
      {(mode === 'gt' || mode === 'lt') && (
        <input
          type="number"
          min="0"
          step="0.01"
          value={amount}
          onChange={(e) => onAmount(e.target.value)}
          placeholder="0.00"
          className="w-20 rounded border border-slate-300 px-2 py-1.5 text-sm"
        />
      )}
    </div>
  );
}
